import {
    Button,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
} from '@fluentui/react-components';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { Dismiss16Regular, MoreHorizontal16Regular } from '@fluentui/react-icons';
import React from 'react';
import {
    container,
    nudgeBar,
    nudgeBarNoImage,
    nudgedIcon,
    iconColumn,
    iconColumnHidden,
    iconSCLFull,
    iconSCLMedium,
    iconSCLCompact,
    iconMCLFull,
    iconMCLMedium,
    iconMCLCompact,
    textColumn,
    textContent,
    contentContainer,
    dismissButtonContainer,
    nudgeActionButton,
    boldText as boldTextStyle,
    bigText,
    smallText,
    full,
    medium,
    compact,
} from './MailListNudge.scss';
import {
    dismissNudgeTooltip,
    mailListNudgeMoreOptionsTooltip,
    nudgeFormattedTextTemplate,
} from './MailListNudge.locstring.json';
import loc, { formatToArray } from 'owa-localize';
import classnames from 'owa-classnames';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { isSingleLineListView } from 'owa-mail-layout';
import { observer } from 'owa-mobx-react';
import isSenderImageEnabled from 'owa-messagelist-layout-option/lib/utils/isSenderImageEnabled';
import type { TableView } from 'owa-mail-list-store';

const dismissIcon = <Dismiss16Regular />;
const moreIcon = <MoreHorizontal16Regular />;

function stopPropagation(evt: React.MouseEvent<unknown>) {
    evt.stopPropagation();
}

export interface EllipsisMenuItem {
    key: string;
    icon?: JSX.Element;
    text: string;
    onClick: (evt: React.MouseEvent<unknown>) => void;
}

export interface MailListNudgeProps {
    icon: React.ReactNode;
    text: React.ReactNode;
    boldText?: string;
    onClick: () => void;
    onDismiss: (evt: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>) => void;
    onRendered?: () => void;
    ellipsisMenuItems?: EllipsisMenuItem[];
    tableView: TableView | undefined;
}

export default observer(function MailListNudge(props: MailListNudgeProps): JSX.Element | null {
    const {
        icon,
        text,
        boldText: boldTextProp,
        onClick,
        onDismiss: onDismiss_0,
        onRendered,
        ellipsisMenuItems,
        tableView,
    } = props;

    const renderedText =
        boldTextProp && typeof text === 'string'
            ? formatToArray(
                  loc(nudgeFormattedTextTemplate),
                  <span key="bold" className={boldTextStyle}>
                      {boldTextProp}
                  </span>,
                  text
              )
            : text;

    const isMCL = isSingleLineListView();
    const isRPDenselessEnabled = isGlobalFeatureEnabled('mon-type-ramp');
    const densityClass = getDensityModeCssClass(full, medium, compact);
    const textDensityClass = getDensityModeCssClass(
        isMCL ? smallText : bigText,
        isMCL ? smallText : bigText,
        isRPDenselessEnabled && !isMCL ? bigText : smallText
    );

    const senderImageEnabled = isSenderImageEnabled();
    const tableChecked = tableView?.isInCheckedMode || tableView?.isInSelectAllState;

    const onKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' && event.target === event.currentTarget) {
                event.stopPropagation();
                onClick();
            }
        },
        [onClick]
    );

    const onDismiss = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            evt.stopPropagation();
            onDismiss_0?.(evt);
        },
        [onDismiss_0]
    );

    React.useEffect(() => {
        onRendered?.();
    }, [onRendered]);

    // - MCL: always show icon
    // - SCL + sender images off: hide icon
    // - SCL + sender images off + checked mode: invisible spacer for checkbox alignment
    // - SCL + sender images on: show icon
    // No useMemo — observer tracks MobX observables (density, sender image setting) directly.
    let renderedIcon: React.ReactNode;
    const sclClassNames = classnames(
        iconColumn,
        getDensityModeCssClass(iconSCLFull, iconSCLMedium, iconSCLCompact)
    );

    if (isMCL) {
        const mclClassNames = classnames(
            iconColumn,
            getDensityModeCssClass(iconMCLFull, iconMCLMedium, iconMCLCompact)
        );
        renderedIcon = (
            <div className={mclClassNames}>
                <span className={nudgedIcon}>{icon}</span>
            </div>
        );
    } else if (
        (!senderImageEnabled && !tableChecked) ||
        (!senderImageEnabled && tableChecked && tableView?.isOnlyOptimisticallyInCheckedMode)
    ) {
        renderedIcon = null;
    } else if (!senderImageEnabled) {
        renderedIcon = <div className={classnames(sclClassNames, iconColumnHidden)} />;
    } else {
        renderedIcon = (
            <div className={sclClassNames}>
                <span className={nudgedIcon}>{icon}</span>
            </div>
        );
    }

    const showIcon = renderedIcon !== null;

    return (
        <div className={classnames(container, densityClass)}>
            <div
                className={classnames(nudgeBar, !showIcon && nudgeBarNoImage)}
                onClick={onClick}
                onKeyDown={onKeyDown}
                tabIndex={0}
            >
                {renderedIcon}
                <div className={textColumn}>
                    <div className={contentContainer}>
                        <span className={classnames(textContent, textDensityClass)}>
                            {renderedText}
                        </span>
                        <div className={dismissButtonContainer}>
                            {ellipsisMenuItems && ellipsisMenuItems.length > 0 && (
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement={true}>
                                        <Button
                                            className={nudgeActionButton}
                                            size="small"
                                            icon={moreIcon}
                                            appearance="transparent"
                                            title={loc(mailListNudgeMoreOptionsTooltip)}
                                            onClick={stopPropagation}
                                            aria-label={loc(mailListNudgeMoreOptionsTooltip)}
                                        />
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            {ellipsisMenuItems.map(item => (
                                                <MenuItem
                                                    key={item.key}
                                                    icon={item.icon}
                                                    onClick={item.onClick}
                                                >
                                                    {item.text}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            )}
                            <Button
                                className={nudgeActionButton}
                                size="small"
                                icon={dismissIcon}
                                appearance="transparent"
                                title={loc(dismissNudgeTooltip)}
                                onClick={onDismiss}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}, 'MailListNudge') as React.FunctionComponent<MailListNudgeProps>;
